import React, { useRef, useState, useEffect, useCallback } from 'react';

// DrawingCanvas — the chat-canvas doodle modal. Strokes are captured in a fixed
// 640×480 logical space (device-independent), rendered live to a 2x canvas, and
// handed to onSend as (pngBlob, strokes). The strokes array is the vector source
// of truth: the chat card re-renders it as crisp SVG, and a future version can
// let the AIs append strokes of their own.
//
// UX notes:
// - Pointer events unify mouse/touch/pen; touch-action:none stops page scroll.
// - Live segments draw incrementally (fast); stroke-end triggers a full smoothed
//   redraw (quadratic midpoints), so long sessions never lag.
// - Destructive actions are two-tap: Clear and close-with-work both ask again.

const LOGICAL_W = 640;
const LOGICAL_H = 480;
const MAX_STROKES = 500;
const MAX_POINTS_PER_STROKE = 1500;
const MIN_POINT_DIST = 1.5; // logical px between captured points

const PALETTE = [
  { color: '#1d1d1f', name: 'Ink' },
  { color: '#ef5350', name: 'Red' },
  { color: '#ff9800', name: 'Orange' },
  { color: '#fdd835', name: 'Yellow' },
  { color: '#66bb6a', name: 'Green' },
  { color: '#42a5f5', name: 'Blue' },
  { color: '#ab47bc', name: 'Purple' },
  { color: '#8d6e63', name: 'Brown' },
  { color: '#ffffff', name: 'Eraser', isEraser: true },
];

const BRUSHES = [
  { size: 3, label: 'Fine' },
  { size: 7, label: 'Marker' },
  { size: 14, label: 'Chunky' },
];

// Draw one stroke with quadratic-midpoint smoothing (shared by live canvas,
// raster export, and mirrored by the SVG path builder in the chat card).
const drawStroke = (ctx, stroke, scale = 1) => {
  const pts = stroke.points;
  if (!pts || pts.length === 0) return;
  ctx.strokeStyle = stroke.color;
  ctx.lineWidth = stroke.size * scale;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.beginPath();
  if (pts.length === 1) {
    // A tap = a dot
    ctx.fillStyle = stroke.color;
    ctx.arc(pts[0][0] * scale, pts[0][1] * scale, (stroke.size * scale) / 2, 0, Math.PI * 2);
    ctx.fill();
    return;
  }
  ctx.moveTo(pts[0][0] * scale, pts[0][1] * scale);
  for (let i = 1; i < pts.length - 1; i++) {
    const midX = ((pts[i][0] + pts[i + 1][0]) / 2) * scale;
    const midY = ((pts[i][1] + pts[i + 1][1]) / 2) * scale;
    ctx.quadraticCurveTo(pts[i][0] * scale, pts[i][1] * scale, midX, midY);
  }
  const last = pts[pts.length - 1];
  ctx.lineTo(last[0] * scale, last[1] * scale);
  ctx.stroke();
};

// `anchored` (desktop): the canvas is a popover that slides UP OUT OF the chat
// input box (positioned ancestor = the composer wrapper) instead of a
// full-screen modal — the doodle is a message, so it grows from where messages
// come from, and the chat stays visible while you draw. Mobile keeps the modal.
// `initialStrokes` (doodle chains): the canvas opens pre-seeded with an existing
// drawing's strokes. The seed is the committed BASELINE — Undo/Clear only touch
// strokes added this session, and Send requires at least one new stroke.
// `maxNewStrokes` (Doodle Bug turns): hard cap on strokes added THIS session
// on top of the baseline — pointer-down stops accepting once hit.
const DrawingCanvas = ({ onSend, onClose, theme = {}, anchored = false, initialStrokes = null, maxNewStrokes = null }) => {
  const canvasRef = useRef(null);
  const baselineRef = useRef((Array.isArray(initialStrokes) ? initialStrokes : [])
    .filter(s => s && Array.isArray(s.points) && s.points.length > 0));
  // Effective cap: turn mode caps new strokes over the seed; free mode keeps
  // the global canvas cap.
  const strokeCap = () => (maxNewStrokes ? baselineRef.current.length + maxNewStrokes : MAX_STROKES);
  const strokesRef = useRef([...baselineRef.current]); // committed strokes (ref: pointer handlers mutate without re-render)
  const currentStrokeRef = useRef(null);
  const lastPointRef = useRef(null);
  const cursorRef = useRef(null);
  // Cached canvas rect + cursor radius. getBoundingClientRect() per pointer
  // move forces a synchronous reflow of the ENTIRE page (GameStepPage is
  // huge), and the cursor ring's width/height writes re-dirty layout right
  // before the next move's gBCR — a forced full reflow per pointer event was
  // what made drawing feel broken. Refresh the rect on the events that can
  // actually move the canvas, never mid-stroke.
  const rectRef = useRef(null);
  const cursorRadiusRef = useRef(0);
  const refreshRect = () => {
    if (canvasRef.current) rectRef.current = canvasRef.current.getBoundingClientRect();
  };
  useEffect(() => {
    window.addEventListener('resize', refreshRect);
    return () => window.removeEventListener('resize', refreshRect);
  }, []);

  // Boolean flags, not a live count: they only flip on 0↔some and
  // full↔not-full transitions, so stroke-end setState almost never
  // re-renders the panel (a per-stroke counter re-rendered it every time)
  const [hasStrokes, setHasStrokes] = useState(false);
  const [canvasFull, setCanvasFull] = useState(false);
  const [color, setColor] = useState(PALETTE[0].color);
  const [brushSize, setBrushSize] = useState(BRUSHES[1].size);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [confirmClear, setConfirmClear] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);

  const isEraser = color === '#ffffff';

  // --- rendering ---
  const redrawAll = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    // desynchronized: ink skips the compositor queue (lower pen latency);
    // the first getContext call fixes the attributes, so keep them identical
    // at every call site
    const ctx = canvas.getContext('2d', { desynchronized: true });
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    const scale = canvas.width / LOGICAL_W;
    strokesRef.current.forEach(s => drawStroke(ctx, s, scale));
    if (currentStrokeRef.current) drawStroke(ctx, currentStrokeRef.current, scale);
  }, []);

  useEffect(() => { redrawAll(); }, [redrawAll]);

  // Brush size changed mid-hover — resize the cursor ring once, here, instead
  // of on every pointer move
  useEffect(() => {
    if (!cursorRef.current || !rectRef.current) return;
    const r = (brushSize * rectRef.current.width / LOGICAL_W) / 2 + 2;
    cursorRadiusRef.current = r;
    cursorRef.current.style.width = `${r * 2}px`;
    cursorRef.current.style.height = `${r * 2}px`;
  }, [brushSize]);

  // --- pointer → logical coords (cached rect — no reflow per move) ---
  const toLogical = (clientX, clientY) => {
    const rect = rectRef.current;
    const x = ((clientX - rect.left) / rect.width) * LOGICAL_W;
    const y = ((clientY - rect.top) / rect.height) * LOGICAL_H;
    return [Math.max(0, Math.min(LOGICAL_W, Math.round(x * 10) / 10)), Math.max(0, Math.min(LOGICAL_H, Math.round(y * 10) / 10))];
  };

  const handlePointerEnter = () => {
    // One measure as the pointer arrives — layout is settled here, so this
    // gBCR is cheap, and every move after it works from the cache
    refreshRect();
    if (cursorRef.current && rectRef.current) {
      const r = (brushSize * rectRef.current.width / LOGICAL_W) / 2 + 2;
      cursorRadiusRef.current = r;
      cursorRef.current.style.width = `${r * 2}px`;
      cursorRef.current.style.height = `${r * 2}px`;
    }
  };

  const handlePointerDown = (e) => {
    if (sending || strokesRef.current.length >= strokeCap()) return;
    e.preventDefault();
    canvasRef.current.setPointerCapture(e.pointerId);
    refreshRect(); // once per stroke — guards against the panel having scrolled
    const p = toLogical(e.clientX, e.clientY);
    currentStrokeRef.current = { color, size: brushSize, points: [p] };
    lastPointRef.current = p;
    redrawAll(); // renders the tap-dot immediately
  };

  const handlePointerMove = (e) => {
    if (!rectRef.current) refreshRect();
    const rect = rectRef.current;
    // Brush cursor follows the pointer even when not drawing. Transform-only
    // (size was set on pointerenter/brush change) — no layout work per move.
    if (cursorRef.current) {
      const r = cursorRadiusRef.current;
      cursorRef.current.style.transform = `translate(${e.clientX - rect.left - r}px, ${e.clientY - rect.top - r}px)`;
      cursorRef.current.style.opacity = '1';
    }
    const stroke = currentStrokeRef.current;
    if (!stroke) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d', { desynchronized: true });
    const scale = canvas.width / LOGICAL_W;
    ctx.strokeStyle = stroke.color;
    ctx.lineWidth = stroke.size * scale;
    ctx.lineCap = 'round';
    // Coalesced events: trackpads/pens report 120-240Hz — the browser batches
    // the between-frame samples, and using them makes fast curves smooth
    // instead of angular, at zero extra frame cost.
    const samples = e.nativeEvent.getCoalescedEvents ? e.nativeEvent.getCoalescedEvents() : [e];
    for (const s of (samples.length ? samples : [e])) {
      const p = toLogical(s.clientX, s.clientY);
      const lp = lastPointRef.current;
      const dx = p[0] - lp[0], dy = p[1] - lp[1];
      if (dx * dx + dy * dy < MIN_POINT_DIST * MIN_POINT_DIST) continue;
      if (stroke.points.length >= MAX_POINTS_PER_STROKE) break;
      stroke.points.push(p);
      lastPointRef.current = p;
      // Incremental segment draw — fast; the smoothed redraw happens on stroke end
      ctx.beginPath();
      ctx.moveTo(lp[0] * scale, lp[1] * scale);
      ctx.lineTo(p[0] * scale, p[1] * scale);
      ctx.stroke();
    }
  };

  const endStroke = () => {
    const stroke = currentStrokeRef.current;
    if (!stroke) return;
    currentStrokeRef.current = null;
    strokesRef.current = [...strokesRef.current, stroke];
    setHasStrokes(true);
    setCanvasFull(strokesRef.current.length >= strokeCap());
    redrawAll();
  };

  const handlePointerLeaveCursor = () => {
    if (cursorRef.current) cursorRef.current.style.opacity = '0';
  };

  // --- actions ---
  const undo = useCallback(() => {
    if (currentStrokeRef.current || strokesRef.current.length <= baselineRef.current.length) return;
    strokesRef.current = strokesRef.current.slice(0, -1);
    setHasStrokes(strokesRef.current.length > baselineRef.current.length);
    setCanvasFull(strokesRef.current.length >= strokeCap());
    redrawAll();
  }, [redrawAll]);

  const clearAll = () => {
    if (!confirmClear) {
      setConfirmClear(true);
      setTimeout(() => setConfirmClear(false), 2500);
      return;
    }
    strokesRef.current = [...baselineRef.current]; // clear YOUR strokes; the seed stays
    currentStrokeRef.current = null;
    setHasStrokes(false);
    setCanvasFull(baselineRef.current.length >= strokeCap());
    setConfirmClear(false);
    redrawAll();
  };

  const requestClose = useCallback(() => {
    if (sending) return;
    if (strokesRef.current.length > baselineRef.current.length && !confirmClose) {
      setConfirmClose(true);
      setTimeout(() => setConfirmClose(false), 2500);
      return;
    }
    onClose();
  }, [sending, confirmClose, onClose]);

  // Esc closes (with the same two-tap guard), Ctrl/Cmd+Z undoes
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') { e.stopPropagation(); requestClose(); }
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') { e.preventDefault(); undo(); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [requestClose, undo]);

  const send = async () => {
    if (sending || strokesRef.current.length <= baselineRef.current.length) return;
    setSending(true);
    setError(null);
    try {
      // Rasterize at 2x on a clean offscreen canvas (the live one may be mid-cursor)
      const off = document.createElement('canvas');
      off.width = LOGICAL_W * 2;
      off.height = LOGICAL_H * 2;
      const ctx = off.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, off.width, off.height);
      strokesRef.current.forEach(s => drawStroke(ctx, s, 2));
      const blob = await new Promise((resolve, reject) =>
        off.toBlob(b => (b ? resolve(b) : reject(new Error('Rasterize failed'))), 'image/png'));
      await onSend(blob, strokesRef.current);
      // Parent closes the modal on success — nothing else to do here
    } catch (err) {
      console.error('[DrawingCanvas] Send failed:', err);
      setError(err.message || 'Failed to send — your drawing is safe, try again');
      setSending(false);
    }
  };

  const accent = theme.accent || '#ec407a';
  const panelBg = theme.bgMedium || '#1a1a2e';
  const textColor = theme.textOnPanel || theme.textDark || '#fff';
  const muted = theme.textMuted || 'rgba(255,255,255,0.55)';

  const iconBtnStyle = (active) => ({
    padding: '6px 12px', borderRadius: '10px', cursor: 'pointer', fontSize: '0.8em', fontWeight: 700,
    backgroundColor: active ? `${accent}28` : 'transparent',
    color: active ? textColor : muted,
    border: `1px solid ${accent}${active ? '55' : '25'}`,
  });

  return (
    <div
      onClick={anchored ? undefined : requestClose}
      style={anchored ? {
        // IN-FLOW, not floating (user, 07-10: the hovering popover still read
        // as a pop-out): the canvas is a normal block above the input inside
        // the composer, so opening it literally EXPANDS the input area and the
        // chat shrinks to make room. No overlay, no scrim, nothing hovers.
        marginBottom: 8,
      } : {
        // z ABOVE the mobile thumb dock (5000) + bottom nav (5100): at 3300 the
        // full-screen canvas was not actually modal on a phone — the nav and
        // composer punched through the scrim, so a tap near the bottom switched
        // tabs behind the open canvas (worst during a timed Doodle Bug turn).
        position: 'fixed', inset: 0, zIndex: 5300,
        // No backdrop blur: re-blurring the whole (animated) space every frame
        // stole the frame budget from the pen — a darker flat scrim reads the same
        backgroundColor: 'rgba(0,0,0,0.72)',
        // No alignItems/justifyContent centering — flex centering CLIPS
        // overflowing content on both ends (the header was cut off on short
        // windows, user report 07-10). The panel centers itself with
        // margin:auto and degrades to scrollable-from-the-top.
        display: 'flex', padding: '16px', overflowY: 'auto',
      }}
    >
      <style>{`
        @keyframes drawCanvasIn { from { opacity: 0; transform: translateY(14px) scale(0.98); } to { opacity: 1; transform: translateY(0) scale(1); } }
        @keyframes drawCanvasUp { from { opacity: 0; transform: translateY(14px) scaleY(0.95); } to { opacity: 1; transform: none; } }
      `}</style>
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          // Width derives from the height budget: the 4:3 canvas + ~175px of
          // header/toolbar chrome must fit WITHOUT scrolling (user call 07-10) —
          // so on short viewports the whole panel narrows instead of scrolling.
          // 175 measured, not guessed: header 26 + palette 30 + footer ~34 +
          // 3 gaps 30 + padding 32 + borders — the old 150 under-counted, so
          // the panel scrolled a little and the header slid out of view on
          // small windows (user report 07-10). The 250px floor keeps the
          // toolbar usable on tiny windows; overflow scrolling is the
          // fallback below that.
          width: anchored
            ? 'min(100%, max(250px, calc((min(56vh, 520px) - 175px) * 4 / 3 + 32px)))'
            : 'min(92vw, 560px, max(250px, calc((92vh - 175px) * 4 / 3 + 32px)))',
          margin: anchored ? '0 auto' : 'auto',
          // In-flow: shares the column with the chat, so cap shorter — the
          // conversation stays visible above while you draw
          maxHeight: anchored ? 'min(56vh, 520px)' : undefined,
          overflowY: anchored ? 'auto' : undefined,
          boxSizing: 'border-box',
          borderRadius: '16px', padding: '16px',
          backgroundColor: panelBg,
          border: `1px solid ${accent}45`,
          // In-flow panel is part of the composer surface — soft lift only,
          // not a floating-card shadow
          boxShadow: anchored
            ? `0 4px 18px rgba(0,0,0,0.28), 0 0 0 1px ${accent}22`
            : '0 16px 56px rgba(0,0,0,0.6)',
          display: 'flex', flexDirection: 'column', gap: '10px',
          transformOrigin: 'bottom center',
          animation: anchored ? 'drawCanvasUp 0.22s ease-out' : 'drawCanvasIn 0.22s ease-out',
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px' }}>
          <div style={{ fontSize: '1em', fontWeight: 800, color: textColor, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>✏️ {baselineRef.current.length > 0 ? 'Add to this doodle' : 'Doodle something for the room'}</div>
          {/* Hint line removed (Nina 07-29: show, don't tell — the reactions ARE the surprise) */}
          <div style={{ flex: 1, minWidth: 0 }} />
          <button
            onClick={requestClose}
            aria-label="Close canvas"
            style={{
              background: 'rgba(0,0,0,0.35)', border: 'none', borderRadius: '50%',
              width: '26px', height: '26px', color: '#fff', cursor: 'pointer',
              fontSize: '0.85em', lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}
          >✕</button>
        </div>

        {/* Canvas */}
        <div style={{ position: 'relative', borderRadius: '10px', overflow: 'hidden', boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.15)' }}>
          <canvas
            ref={canvasRef}
            width={LOGICAL_W * 2}
            height={LOGICAL_H * 2}
            onPointerEnter={handlePointerEnter}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={endStroke}
            onPointerCancel={endStroke}
            onPointerLeave={handlePointerLeaveCursor}
            style={{
              display: 'block', width: '100%', height: 'auto',
              touchAction: 'none', cursor: 'none',
              backgroundColor: '#fff',
            }}
          />
          {/* Brush-size cursor ring */}
          <div
            ref={cursorRef}
            style={{
              position: 'absolute', top: 0, left: 0, borderRadius: '50%',
              border: `1.5px solid ${isEraser ? '#999' : color}`,
              backgroundColor: isEraser ? 'rgba(255,255,255,0.6)' : `${color}22`,
              pointerEvents: 'none', opacity: 0, transition: 'opacity 0.15s',
            }}
          />
          {canvasFull && (
            <div style={{
              position: 'absolute', bottom: '8px', left: '50%', transform: 'translateX(-50%)',
              fontSize: '0.7em', fontWeight: 700, color: '#fff', backgroundColor: 'rgba(0,0,0,0.6)',
              padding: '4px 10px', borderRadius: '10px',
            }}>{maxNewStrokes ? `${maxNewStrokes} strokes max — make them count! Send it.` : 'Canvas is full — a true masterpiece. Send it!'}</div>
          )}
        </div>

        {/* Palette + brushes */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: '5px', flex: 1, flexWrap: 'wrap' }}>
            {PALETTE.map(p => (
              <button
                key={p.color}
                onClick={() => setColor(p.color)}
                title={p.name}
                aria-label={p.name}
                style={{
                  width: '26px', height: '26px', borderRadius: '50%', cursor: 'pointer', padding: 0,
                  backgroundColor: p.color,
                  border: p.isEraser ? '1.5px dashed #999' : `2px solid ${color === p.color ? '#fff' : 'rgba(255,255,255,0.2)'}`,
                  outline: color === p.color ? `2px solid ${accent}` : 'none',
                  transform: color === p.color ? 'scale(1.18)' : 'scale(1)',
                  transition: 'transform 0.12s ease',
                  fontSize: '0.6em',
                }}
              >{p.isEraser ? '🧽' : ''}</button>
            ))}
          </div>
          <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
            {BRUSHES.map(b => (
              <button
                key={b.size}
                onClick={() => setBrushSize(b.size)}
                title={b.label}
                aria-label={b.label}
                style={{
                  width: '30px', height: '30px', borderRadius: '8px', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0,
                  backgroundColor: brushSize === b.size ? `${accent}28` : 'transparent',
                  border: `1px solid ${accent}${brushSize === b.size ? '55' : '25'}`,
                }}
              >
                <span style={{
                  width: `${4 + b.size}px`, height: `${4 + b.size}px`, borderRadius: '50%',
                  backgroundColor: isEraser ? '#999' : color === '#ffffff' ? '#999' : color,
                  display: 'block',
                }} />
              </button>
            ))}
          </div>
        </div>

        {/* Footer: undo/clear + cancel/send. Wraps on narrow panels — the five
            items squished into unreadable slivers on small windows (user, 07-10)
            📌 STICKY in anchored mode (Nina 08-14: "I can't scroll down to click
            send on a narrower desktop"). The panel scrolls internally, but the
            canvas swallows wheel/pointer events to draw with — so the one
            gesture that would reveal the footer is the one that draws instead.
            Pinning it to the panel's bottom edge means Send is reachable no
            matter where the panel's scroll sits. Negative bottom + padding eat
            the panel's own 16px pad so nothing shows underneath. */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', justifyContent: 'flex-end',
          ...(anchored ? {
            position: 'sticky', bottom: -16, marginTop: 'auto',
            background: panelBg, paddingTop: 8, paddingBottom: 16, marginBottom: -16,
            zIndex: 1,
          } : {}),
        }}>
          <button onClick={undo} disabled={!hasStrokes || sending} title="Undo" aria-label="Undo" style={{ ...iconBtnStyle(false), opacity: !hasStrokes ? 0.4 : 1 }}>↩️</button>
          <button onClick={clearAll} disabled={!hasStrokes || sending} title="Clear" aria-label="Clear" style={{ ...iconBtnStyle(confirmClear), opacity: !hasStrokes ? 0.4 : 1, color: confirmClear ? '#ef5350' : undefined }}>
            {confirmClear ? 'Wipe it all?' : '🗑️'}
          </button>
          <div style={{ flex: 1, fontSize: '0.62em', color: muted, textAlign: 'center' }}>
            {error ? <span style={{ color: '#ef5350', fontWeight: 700 }}>{error}</span>
              : confirmClose ? <span style={{ color: '#ff9800', fontWeight: 700 }}>Close and lose the doodle? Tap again.</span>
                : ''}
          </div>
          <button onClick={requestClose} disabled={sending} style={iconBtnStyle(false)}>Cancel</button>
          <button
            onClick={send}
            disabled={!hasStrokes || sending}
            style={{
              padding: '8px 20px', borderRadius: '10px', cursor: !hasStrokes || sending ? 'default' : 'pointer',
              fontSize: '0.85em', fontWeight: 800, border: 'none',
              background: !hasStrokes ? `${accent}33` : `linear-gradient(135deg, ${accent}, ${accent}bb)`,
              color: '#fff', opacity: sending ? 0.7 : 1,
              boxShadow: hasStrokes && !sending ? `0 3px 14px ${accent}55` : 'none',
            }}
          >{sending ? 'Sending…' : '📤 Send it'}</button>
        </div>
      </div>
    </div>
  );
};

export default DrawingCanvas;
